"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateTapeBuilderNodeGraph = void 0;
const common_1 = require("@finley-technologies/common");
const uiNodes_1 = require("../uiNodes/uiNodes");
const utils_1 = require("../uiNodes/utils");
// assumes that all given nodes are connected in a graph.
const ensureUniqueMeshName = ({ nodes, }) => {
    const errors = [];
    // only raw tapes have uploadConfig. This typeguards help
    // TS narrow to types that does have uploadConfig.
    const meshNames = nodes
        .map((node) => {
        if (uiNodes_1.tapeNodes.file_rawCashBalanceFeed.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawLoanTape.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawRepaymentTape.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawSupplementalTape.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawZipTape.isNode(node)) {
            return node.configuration.uploadConfig?.meshName ?? null;
        }
        return null;
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
    const uniqueMeshNames = new Set(meshNames);
    if (uniqueMeshNames.size >= 2) {
        errors.push(`All connected nodes must share same meshName but found ${Array.from(uniqueMeshNames).join(',')}`);
    }
    return errors;
};
// given a tape builder graph, split it into disjoint subgraphs.
const splitDisjointGraphs = (tbGraph) => {
    const idToNode = new Map();
    tbGraph.nodes.forEach((node) => {
        idToNode.set(node.metadata.tapeBuilderNodeId, node);
    });
    const getNodeById = (id) => idToNode.get(id);
    const getDirectConnections = (nodeId) => {
        return tbGraph.edges
            .filter(({ source, target }) => source === nodeId || target === nodeId)
            .map(({ source, target }) => (source === nodeId ? target : source));
    };
    const visited = new Set();
    const dfs = (node, nodes = []) => {
        if (!visited.has(node)) {
            visited.add(node);
            nodes.push(node);
            const connections = getDirectConnections(node);
            connections.forEach((connection) => {
                dfs(connection, nodes);
            });
        }
        return nodes;
    };
    // split the graph into disjoint subgraphs
    const graphs = [];
    idToNode.forEach((_, id) => {
        if (!visited.has(id)) {
            const nodes = dfs(id);
            graphs.push(nodes);
        }
    });
    // convert each set of connected nodes to TapeBuilderNodeGraph.
    return graphs.map((connectedNodes) => {
        const setOfNodes = new Set(connectedNodes);
        const edges = tbGraph.edges.filter(({ source, target }) => setOfNodes.has(source) && setOfNodes.has(target));
        const nodes = connectedNodes.map(getNodeById);
        return { nodes, edges };
    });
};
const validateTapeBuilderNodeGraph = ({ graph, }) => {
    //
    // we will do any generic graph validations here
    // and then ask each node to validate itself.
    //
    const { nodes } = graph;
    const errors = [];
    //
    // all graph nodes have unique tapeBuilderNodeId
    //
    const tapeBuilderNodeIds = nodes.map((node) => node.metadata.tapeBuilderNodeId);
    const uniqueTapeBuilderNodeIds = new Set(tapeBuilderNodeIds);
    if (tapeBuilderNodeIds.length !== uniqueTapeBuilderNodeIds.size) {
        errors.push(`All graph nodes must have unique tapeBuilderNodeId. Found ${tapeBuilderNodeIds.join(',')}`);
    }
    //
    // all raw nodes must have unique externalFilename
    //
    const externalFileNames = nodes
        .map((node) => {
        return uiNodes_1.tapeNodes.file_rawCashBalanceFeed.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawReceivableDocumentation.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawLoanTape.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawRepaymentTape.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawSupplementalTape.isNode(node) ||
            uiNodes_1.tapeNodes.file_rawZipTape.isNode(node)
            ? node.configuration.externalFilename
            : null;
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
    // we need all external filenames Not only to be different but also to have unique prefix.
    // that mean we cannot have externalFilename: "loanTape", and externalFilename: "loanTape1" in the same graph.
    // ensure that all external filenames have unique prefix.
    // for this we sort the external filenames and then check if
    // any two consecutive filenames have same prefix.
    const sortedExternalFileNames = [...externalFileNames].sort();
    // eslint-disable-next-line no-restricted-syntax, finley/variable-length, no-plusplus
    for (let i = 0; i < sortedExternalFileNames.length - 1; i++) {
        const current = sortedExternalFileNames[i];
        const next = sortedExternalFileNames[i + 1];
        if (current.startsWith(next) || next.startsWith(current)) {
            errors.push(`All raw nodes must have unique externalFilename prefix. Found [${current}] and [${next}]`);
        }
    }
    //
    // all tape nodes must have unique artifactSlug
    //
    const slugs = nodes
        .map((node) => {
        const uiNode = (0, uiNodes_1.getUINodeByType)(node.metadata.tapeBuilderNodeType);
        return uiNode.isOperation ? null : uiNode.getArtifactSlug(node);
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
    const uniqueSlug = new Set(slugs);
    if (slugs.length !== uniqueSlug.size) {
        errors.push(`All tape nodes must have unique artifactSlug. Found ${slugs.join(',')}`);
    }
    const resolve = (node) => ({
        node,
        uiNode: (0, uiNodes_1.getUINodeByType)(node.metadata.tapeBuilderNodeType),
    });
    const allNodes = nodes.map(resolve);
    // let node implementations validate their own config.
    allNodes.forEach(({ node, uiNode }) => {
        const nodeErrors = uiNode.hasValidConfig({
            graph,
            node,
            friendlyName: uiNode.getFriendlyName(node),
            inputs: (0, utils_1.getSourceNodes)({ node, graph }).map(resolve),
            outputs: (0, utils_1.getTargetNodes)({ node, graph }).map(resolve),
            allNodes,
        });
        errors.push(...nodeErrors);
    });
    // to validate that we have unique meshName for all connected nodes.
    // split the graph into disconnected subgraphs
    const subGraphs = splitDisjointGraphs(graph);
    // ensure unique name for each disconnected subgraph
    subGraphs.forEach((subGraph) => {
        errors.push(...ensureUniqueMeshName({ nodes: subGraph.nodes }));
    });
    return errors;
};
exports.validateTapeBuilderNodeGraph = validateTapeBuilderNodeGraph;
//# sourceMappingURL=validateTapeBuilderNodeGraph.js.map